import { useQueryClient } from "@tanstack/react-query"
import { EditInsuranceFormData, ResourceObjectInsurance } from "../types"
import {
  useCreateResourceObjectInsurance,
  useUpdateResourceObjectInsurance,
  useDeleteResourceObjectInsurance,
} from "@/orval/gen"

interface UseInsuranceLogicProps {
  resourceObjectId: number
}

export function useInsuranceLogic({
  resourceObjectId,
}: UseInsuranceLogicProps) {
  const queryClient = useQueryClient()

  const [isEditInsuranceModalOpen, setIsEditInsuranceModalOpen] =
    React.useState(false)
  const [isCreateInsuranceModalOpen, setIsCreateInsuranceModalOpen] =
    React.useState(false)
  const [selectedInsuranceForEdit, setSelectedInsuranceForEdit] =
    React.useState<ResourceObjectInsurance | null>(null)
  const [editInsuranceFormData, setEditInsuranceFormData] =
    React.useState<EditInsuranceFormData>({
      insuredPersonId: null,
      startDate: null,
      endDate: null,
      isPrivateInsurance: true,
    })

  React.useEffect(() => {
    if (isEditInsuranceModalOpen && selectedInsuranceForEdit) {
      setEditInsuranceFormData({
        insuredPersonId: selectedInsuranceForEdit.insuredPersonId ?? null,
        startDate: selectedInsuranceForEdit.startDate
          ? new Date(selectedInsuranceForEdit.startDate)
          : null,
        endDate: selectedInsuranceForEdit.endDate
          ? new Date(selectedInsuranceForEdit.endDate)
          : null,
        isPrivateInsurance: selectedInsuranceForEdit.isPrivateInsurance ?? true,
      })
    }
  }, [isEditInsuranceModalOpen, selectedInsuranceForEdit])

  React.useEffect(() => {
    if (isCreateInsuranceModalOpen) {
      setEditInsuranceFormData({
        insuredPersonId: null,
        startDate: new Date(),
        endDate: null,
        isPrivateInsurance: true,
      })
    }
  }, [isCreateInsuranceModalOpen])

  const createInsuranceApiMutation = useCreateResourceObjectInsurance({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["resourceObjectInsurances", resourceObjectId],
        })
        setIsCreateInsuranceModalOpen(false)
      },
    },
  })

  const updateInsuranceApiMutation = useUpdateResourceObjectInsurance({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["resourceObjectInsurances", resourceObjectId],
        })
        setIsEditInsuranceModalOpen(false)
        setSelectedInsuranceForEdit(null)
      },
    },
  })

  const deleteInsuranceApiMutation = useDeleteResourceObjectInsurance({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["resourceObjectInsurances", resourceObjectId],
        })
        setIsEditInsuranceModalOpen(false)
        setSelectedInsuranceForEdit(null)
      },
    },
  })

  const handleCreateInsurance = (data: EditInsuranceFormData) => {
    const payload = {
      resourceObjectId,
      insuredPersonId: data.insuredPersonId!,
      startDate: data.startDate
        ? new Date(data.startDate).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0],
      endDate: data.endDate
        ? new Date(data.endDate).toISOString().split("T")[0]
        : undefined,
      isPrivateInsurance: data.isPrivateInsurance,
    }
    createInsuranceApiMutation.mutate({ data: payload })
  }

  const handleUpdateInsurance = (data: EditInsuranceFormData) => {
    if (!selectedInsuranceForEdit?.resourceObjectInsuranceId) {
      throw new Error("No insurance selected")
    }

    const payload = {
      resourceObjectInsuranceId:
        selectedInsuranceForEdit.resourceObjectInsuranceId,
      resourceObjectId,
      insuredPersonId: data.insuredPersonId!,
      startDate: data.startDate
        ? new Date(data.startDate).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0],
      endDate: data.endDate
        ? new Date(data.endDate).toISOString().split("T")[0]
        : undefined,
      isPrivateInsurance: data.isPrivateInsurance,
    }
    updateInsuranceApiMutation.mutate({ data: payload })
  }

  const handleDeleteInsurance = (insuranceId: number) => {
    const payload = {
      resourceObjectInsuranceId: insuranceId,
      resourceObjectId,
    }
    deleteInsuranceApiMutation.mutate({ data: payload })
  }

  return {
    isEditInsuranceModalOpen,
    setIsEditInsuranceModalOpen,
    isCreateInsuranceModalOpen,
    setIsCreateInsuranceModalOpen,
    selectedInsuranceForEdit,
    setSelectedInsuranceForEdit,
    editInsuranceFormData,
    setEditInsuranceFormData,
    updateInsuranceMutation: updateInsuranceApiMutation,
    createInsuranceMutation: createInsuranceApiMutation,
    deleteInsuranceMutation: deleteInsuranceApiMutation,
    handleCreateInsurance,
    handleUpdateInsurance,
    handleDeleteInsurance,
  }
}
